import React, { JSX } from "react";
import { Typography, Box } from "@mui/material";
import { chain, round } from "mathjs";
import startCase from "lodash/startCase";
import moment from "moment";
import "./PrintBill.scss";
import { useSelector } from "react-redux";
import {
  BillItemType,
  BillType,
  DiscountBasedOn,
  DocumentTypes
} from "../../../interfaces/BillInterfaces";
import { rupeeDisplay } from "../../../helpers/rupee";
import { scaleFont } from "../../../components/Print/Print";
import { tl, t } from "../../../components/translate";
import classNames from "../../../helpers/classNames";
import { spFromIdSelector, spFullNameSelectorWithNmc } from "../../../reducers/serviceProvider";
import { getSectionStyle } from "../../Lab/LabPrint/LabPrintFunctions";
import { RootState } from "../../../store";
import { ForceTemplate } from "./BillPrintHelpers";
import capitalizedName from "../../../helpers/nameCapitalizer";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import {
  BillColumns,
  getBillColumns,
  showUnitAfterQuantity,
  showInfoColumn,
  marginForNameColumn,
  getColumnTextStyles,
  hasItemWithHSCode
} from "../../../actions/helpers/billHelper";
import { getPaymentMethodLabel } from "../../Reports/SalesReport";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { useShowStockBatchAndExpiryInBillPrint } from "../../../hooks/useShowStockBatchAndExpiryInBillPrint";

const SummaryFieldBankPaper = ({
  label,
  value,
  labelStyle,
  valueStyle,
  alignLeft
}: {
  label: string | React.ReactNode;
  value: number | string;
  labelStyle?: { [key: string]: string };
  valueStyle?: { [key: string]: string };
  alignLeft?: boolean;
}) => {
  const data = typeof value === "number" ? rupeeDisplay(round(Number(value || 0), 2)) : value;
  return (
    <Box display="flex" justifyContent={`${alignLeft ? "flex-start" : "flex-end"}`} width="100%">
      <Typography fontSize="0.23cm" fontWeight={500} sx={{ ...labelStyle }}>
        {label}
      </Typography>
      <Typography fontSize="0.28cm" width="2.5cm" fontWeight={500} sx={{ ...valueStyle }}>
        {data}
      </Typography>
    </Box>
  );
};

SummaryFieldBankPaper.defaultProps = {
  labelStyle: {},
  valueStyle: {},
  alignLeft: false
};

const BillDescriptionBankPaper = ({
  billData,
  SPs,
  relatedSettings,
  clientBalance,
  forceTemplate,
  hidePaymentMethodOnPrint
}: {
  billData: BillType;
  SPs: ServiceProvider[];
  relatedSettings: { [key: string]: string };
  clientBalance: number;
  forceTemplate: string;
  hidePaymentMethodOnPrint: boolean;
}): JSX.Element | null => {
  const currentRc = useCurrentResourceCentre();
  const { showStockBatchAndExpiryInBillPrint } = useShowStockBatchAndExpiryInBillPrint();

  // selectors
  const user = useSelector((state: RootState) => state.userContext.user);
  const isVatRegistered = useSelector(
    (state: RootState) => state.userContext.resourceCentre.settings.billingSettings.vatRegistered
  );

  // variables
  const fontSize = "0.26cm";
  const showInfo = showInfoColumn(billData);
  const isBankPaper = forceTemplate === ForceTemplate.BANK_PAPER;
  const showSubItemPriceOnPrint = currentRc?.settings?.billingSettings?.showSubItemPriceOnPrint;
  const showHSCode = hasItemWithHSCode(billData);

  if (!billData || !billData.document) return null;

  const billColumns = getBillColumns(showInfo).filter((header) => {
    if (header === BillColumns.hsCode) {
      return showHSCode;
    }

    if (header === BillColumns.DISCOUNT_AMT) {
      return !(
        [BillColumns.DISCOUNT_AMT].includes(header) &&
        billData.document?.settings.discountSettings.discountBasis === DiscountBasedOn.invoice
      );
    }
    if (header === BillColumns.VAT_PCT) {
      if (isVatRegistered) {
        return header;
      }
      return false;
    }
    return header;
  });

  const hasNewTaxSummary = billData?.summary && "vatAmtWithRates" in billData.summary;

  const showLegacyVat =
    Object.keys(billData?.document?.summary?.taxAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  const showVatByRate =
    Object.keys(billData?.document?.summary?.vatAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  let netTotal;
  if (billData.document.type === DocumentTypes.CREDITNOTE) {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .add(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  } else {
    netTotal = billData.document.billItems.reduce(
      (sum, item) =>
        chain(sum)
          .add(
            chain(item.perUnit || 0)
              .multiply(item.quantity || 1)
              .subtract(item.discountAmt || 0)
              .done()
          )
          .done(),
      0
    );
  }

  const spField = (item: BillItemType) =>
    Array.isArray(item?.serviceProviderIds) && item.serviceProviderIds.length > 0 ? (
      <Box component="span" sx={{ ml: "4px" }}>
        (
        {item.serviceProviderIds
          .map((spId) => spFullNameSelectorWithNmc(spFromIdSelector(SPs, spId)))
          .join(", ")}
        )
      </Box>
    ) : (
      item?.serviceProviderId && (
        <Box component="span" sx={{ ml: "4px" }}>
          ({spFullNameSelectorWithNmc(spFromIdSelector(SPs, item.serviceProviderId))})
        </Box>
      )
    );

  return (
    <div>
      <div>
        <Box
          display="flex"
          borderTop="1px solid #bbb"
          borderRight={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderBottom={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          borderLeft={relatedSettings.isBordered ? "1px solid #bbb" : undefined}
          py="0.2cm"
          className={!relatedSettings.isBordered ? "borderBotBlack1" : undefined}
          sx={
            relatedSettings.isBordered
              ? getSectionStyle(relatedSettings.headerColor).rawHeaderStyle
              : {}
          }
        >
          {billColumns.map((column, i) => (
            <Box
              component="div"
              key={column}
              className={classNames(
                [BillColumns.SNO, BillColumns.QUANTITY, BillColumns.PER_UNIT].includes(column) &&
                  "flex1",

                [
                  BillColumns.hsCode,
                  BillColumns.DISCOUNT_AMT,
                  BillColumns.VAT_PCT,
                  BillColumns.GROSS_TOTAL
                ].includes(column) && "flex2",

                [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
              )}
              sx={{
                mx: marginForNameColumn(column),
                px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : ""
              }}
            >
              <Typography
                sx={{
                  fontSize: scaleFont("0.27cm", 0.85),
                  textTransform: "uppercase",
                  whiteSpace: "pre",
                  textAlign: getColumnTextStyles({ index: i, column }).textAlign,
                  fontWeight: 600
                }}
              >
                {tl(`billing.billItem.${column}`)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box component="div" paddingBottom="0.45cm">
          {[...billData.document.billItems]
            .sort(
              (a: Partial<BillItemType>, b: Partial<BillItemType>) => (a.sNo ?? 0) - (b.sNo ?? 0)
            )
            .map((item, index) => (
              <Box
                component="div"
                key={item.sNo}
                display="flex"
                style={
                  relatedSettings.isBordered
                    ? {
                        pageBreakInside: "avoid"
                      }
                    : {}
                }
                borderTop={relatedSettings.isBordered && index !== 0 ? "0.5px solid lightgrey" : ""}
                borderBottom="1px solid #e0e0e0"
                className={
                  index === billData.document.billItems.length - 1 &&
                  !relatedSettings.isBordered &&
                  "borderBotBlack1"
                }
              >
                {billColumns.map((column, i) => (
                  <Box
                    component="div"
                    key={column}
                    className={classNames(
                      [BillColumns.SNO, BillColumns.QUANTITY, BillColumns.PER_UNIT].includes(
                        column
                      ) && "flex1",

                      [
                        BillColumns.hsCode,
                        BillColumns.DISCOUNT_AMT,
                        BillColumns.VAT_PCT,
                        BillColumns.GROSS_TOTAL
                      ].includes(column) && "flex2",

                      [BillColumns.DESCRIPTION, BillColumns.INFO].includes(column) && "flex3"
                    )}
                    sx={{
                      py: "0.2cm",
                      mx: marginForNameColumn(column),
                      borderLeft: i === 0 && relatedSettings.isBordered ? "1px solid #bbb" : "",
                      borderRight:
                        i === billColumns.length - 1 && relatedSettings.isBordered
                          ? "1px solid #bbb"
                          : "",
                      px: i === 0 || i === billColumns.length - 1 ? "0.2cm" : ""
                    }}
                  >
                    <Box
                      sx={getColumnTextStyles({
                        index: i,
                        column,
                        fontSize: scaleFont("0.23cm", 1)
                      })}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {["perUnit", "grossTotal"].includes(column) && item[column] !== null
                        ? Number(item[column]).toFixed(2)
                        : item[column]
                        ? item[column]
                        : "-"}

                      {showUnitAfterQuantity(column) && item.unit}

                      {column === BillColumns.DESCRIPTION && (
                        <Box component="span" sx={{ ml: "4px", fontSize: "10px" }}>
                          {(item?.teeth?.length ?? 0) > 0 && `(Teeth: ${item?.teeth?.join(", ")})`}
                        </Box>
                      )}

                      {column === "description" && (
                        <Box fontSize={scaleFont("0.23cm", 0.89)}>
                          {item.serviceCode && ` (${item.serviceCode})`}
                        </Box>
                      )}
                      {column === BillColumns.DESCRIPTION && spField(item)}
                    </Box>

                    {column === "description" && showStockBatchAndExpiryInBillPrint && (
                      <Box sx={{ whiteSpace: "pre-wrap" }} fontSize={scaleFont("0.23cm", 0.89)}>
                        {(item?.batchId || item.batchInfo?.batchId) &&
                          `Batch ${item?.batchId || item.batchInfo?.batchId}`}
                        {(item?.expiryDate || item.batchInfo?.expiryDate) &&
                          `, Expiring ${moment(
                            item?.expiryDate || item.batchInfo?.expiryDate
                          ).format("YYYY/MM/DD")}`}
                      </Box>
                    )}

                    {column === "description" &&
                      item?.subItems?.map((subitem) => (
                        <Box
                          key={subitem.productId}
                          paddingTop="0.05cm"
                          className={classNames("blockContent", { bankPaperFontSize: isBankPaper })}
                        >
                          {`◦ ${subitem.description}`}
                          {spField(subitem)}
                        </Box>
                      ))}
                    {showSubItemPriceOnPrint &&
                      column === "perUnit" &&
                      item?.subItems?.map((subitem) => (
                        <Typography
                          key={subitem.productId}
                          textAlign="right"
                          paddingTop="0.05cm"
                          fontSize="0.19cm"
                        >
                          {`${subitem.productData?.grossTotalPrice}`}
                        </Typography>
                      ))}
                  </Box>
                ))}
              </Box>
            ))}
        </Box>
      </div>
      <Box display="flex" className="blockContent">
        <Box width="60%" pr={0.5}>
          {!hidePaymentMethodOnPrint &&
            (Boolean(billData?.paymentInfo?.paymentMethod) ||
              billData?.paymentInfo?.paymentBreakdown?.length >= 1) && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={fontSize} flexShrink={0}>
                  {tl("billing.paymentMethod")}:
                </Typography>
                <Typography fontSize={isBankPaper ? "0.23cm" : scaleFont("0.26cm")} flexGrow={1}>
                  {startCase(
                    getPaymentMethodLabel({
                      paymentMethod: billData?.paymentInfo?.paymentMethod ?? null,
                      paymentBreakdown: billData.paymentInfo.paymentBreakdown ?? []
                    })
                  )}
                </Typography>
              </Box>
            )}
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Typography fontSize={fontSize} flexShrink={0}>
              {tl("billing.inWords")}:
            </Typography>
            <Typography fontSize={fontSize} flexGrow={1}>
              {billData.document.summary.inWords} only
            </Typography>
          </Box>

          <Typography fontWeight={400} fontSize={scaleFont("0.23cm")} />

          <Box mt={2}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Typography fontSize={fontSize} flexShrink={0}>
                {tl("billing.printedDateTime")}:
              </Typography>
              <Typography fontSize={fontSize} flexGrow={1}>
                {`${moment().format("YYYY/MM/DD")} ${moment().format("h:mm a")}`}
              </Typography>
            </Box>
            {billData?.printCount > 0 && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={fontSize} flexShrink={0}>
                  {tl("billing.rePrintUser")}:
                </Typography>
                <Typography fontSize={fontSize} flexGrow={1}>
                  {capitalizedName(`${user?.firstName} ${user?.lastName}`)}
                </Typography>
              </Box>
            )}
            {billData.document.enteredBy.name && (
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography fontSize={fontSize} flexShrink={0}>
                  {tl("billing.billedBy")}:
                </Typography>
                <Typography fontSize={fontSize} flexGrow={1}>
                  {billData.document.enteredBy.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box width="40%">
          <Box paddingBottom="0.2cm" width="100%" textAlign="right">
            {Boolean(billData.document.summary.totalPerUnitTimesQty) && (
              <SummaryFieldBankPaper
                label={tl("billing.total")}
                value={billData.document.summary.totalPerUnitTimesQty}
              />
            )}
            {billData.document.summary.discount > 0 && (
              <SummaryFieldBankPaper
                label={tl("billing.discount")}
                value={billData.document.summary.discount || 0}
              />
            )}

            {!hasNewTaxSummary &&
              showLegacyVat &&
              Object.keys(billData?.summary?.taxAmtWithRates || {})
                .filter((vatPct) => Number(vatPct) !== 0)
                .map((vatPct, index) => (
                  <SummaryFieldBankPaper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    label={<>VAT {vatPct}%</>}
                    value={billData?.summary?.taxAmtWithRates?.[vatPct]?.toFixed(2) || ""}
                  />
                ))}

            {hasNewTaxSummary &&
              showVatByRate &&
              Object.keys(billData?.summary?.vatAmtWithRates || {})
                .filter((vatPct) => Number(vatPct) !== 0)
                .map((vatPct, index) => (
                  <SummaryFieldBankPaper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    label={<>VAT {vatPct}%</>}
                    value={billData?.summary?.vatAmtWithRates?.[vatPct]?.toFixed(2) || ""}
                  />
                ))}

            {Boolean(billData.document.summary.taxAmount) && (
              <SummaryFieldBankPaper
                label="Tax Amount"
                value={billData.document.summary.taxAmount}
              />
            )}

            {netTotal > 0 && (
              <Box marginTop="0.1cm">
                <SummaryFieldBankPaper label={tl("billing.taxableAmount")} value={netTotal} />
              </Box>
            )}
          </Box>

          <Box width="100%" textAlign="right">
            <SummaryFieldBankPaper
              labelStyle={{ fontSize }}
              valueStyle={{ fontSize }}
              label={tl("billing.grandTotal")}
              value={rupeeDisplay(billData.document.summary.totalAmount.toFixed(2))}
            />
            {clientBalance > 0 && (
              <SummaryFieldBankPaper
                labelStyle={{ fontSize }}
                valueStyle={{ fontSize }}
                label={t("billing.advanceRemaining")}
                value={rupeeDisplay(clientBalance.toFixed(2))}
              />
            )}
            {!!billData?.document?.paymentInfo?.paymentDistribution?.paymentFromSsf && (
              <SummaryFieldBankPaper
                labelStyle={{ fontSize }}
                valueStyle={{ fontSize }}
                label={t("billing.paymentFromSSF")}
                value={rupeeDisplay(
                  billData.document.paymentInfo.paymentDistribution.paymentFromSsf
                )}
              />
            )}
            {billData.paymentInfo?.paidAmount > 0 && (
              <SummaryFieldBankPaper
                labelStyle={{ fontSize }}
                valueStyle={{ fontSize }}
                label={t("reports.paidAmount")}
                value={round(billData.paymentInfo?.paidAmount, 2)}
              />
            )}
            {!billData.paymentInfo?.paid && (
              <SummaryFieldBankPaper
                labelStyle={{ fontSize }}
                valueStyle={{ fontSize }}
                label={t("reports.dueAmount")}
                value={round(
                  chain(billData.summary?.totalAmount)
                    .subtract(billData.paymentInfo?.paidAmount || 0)
                    .done(),
                  2
                )}
              />
            )}
            {!!billData.paymentInfo?.tenderAmount && (
              <>
                <SummaryFieldBankPaper
                  labelStyle={{ fontSize }}
                  valueStyle={{ fontSize }}
                  label={t("billing.tenderAmount")}
                  value={rupeeDisplay(billData.paymentInfo.tenderAmount.toFixed(2))}
                />
                <SummaryFieldBankPaper
                  labelStyle={{ fontSize }}
                  valueStyle={{ fontSize }}
                  label={t("billing.changeAmount")}
                  value={rupeeDisplay(billData.paymentInfo.changeAmount.toFixed(2))}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default BillDescriptionBankPaper;
